import React from 'react';
import type {Viewport} from '@packages/systems/open-canvas/core';

/**
 * The id of the element on which to apply the viewport transformations to.
 */
export const OPEN_CANVAS_ROOT_ID = 'wf-open-canvas-root';
export const OPEN_CANVAS_VIEWPORT_SCALE_CSS_VARIABLE =
  '--wf-open-canvas-viewport-scale';

type ViewportStylesProps = {
  viewport: Viewport;
  targetDocument: Document;
};

/**
 * Applies the viewport transformation directly to the Open Canvas root.
 * Rewriting a stylesheet rule invalidates styles on every pan frame.
 */
export const ViewportStyles = ({
  viewport,
  targetDocument,
}: ViewportStylesProps) => {
  const {x, y, k} = viewport;

  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const isReadyRef = React.useRef(false);

  React.useLayoutEffect(() => {
    const root = targetDocument.getElementById(OPEN_CANVAS_ROOT_ID);
    if (!root) return;

    root.style.transform = `translateX(${-x * k}px) translateY(${-y * k}px) scale(${k})`;
  }, [k, targetDocument, x, y]);

  React.useLayoutEffect(() => {
    const root = targetDocument.getElementById(OPEN_CANVAS_ROOT_ID);
    if (!root) return;

    root.style.setProperty(OPEN_CANVAS_VIEWPORT_SCALE_CSS_VARIABLE, `${k}`);
  }, [k, targetDocument]);

  /**
   * This effect is responsible for toggling the `will-change` property on the canvas root element during viewport changes.
   * This is done to avoid layout thrashing during viewport changes. Note
   *
   * Setting `will-change: transform` ensures optimized performance during pan/zoom. However we are only setting
   * this value during the interaction itself as otherwise it results in blurry or pixelated content specifically on higher zoom levels.
   */
  React.useEffect(() => {
    const root = targetDocument.getElementById(OPEN_CANVAS_ROOT_ID);
    if (!root) return;

    // Skip running this effect on initial render. We're only interested
    // in triggering this once the user initiated pan/zoom.
    if (!isReadyRef.current) {
      isReadyRef.current = true;
      root.style.willChange = 'auto';
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    root.style.willChange = 'transform';
    timeoutRef.current = setTimeout(() => {
      root.style.willChange = 'auto';
      timeoutRef.current = undefined;
    }, 100);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [targetDocument, viewport]);

  React.useEffect(() => {
    return () => {
      const root = targetDocument?.getElementById(OPEN_CANVAS_ROOT_ID);
      if (!root) return;

      root.style.transform = '';
      root.style.removeProperty(OPEN_CANVAS_VIEWPORT_SCALE_CSS_VARIABLE);
      root.style.willChange = '';
    };
  }, [targetDocument]);

  return null;
};
